import * as React from "react";
import { GameTag } from "../../../http/protocol";
import { t } from "../../../i18n";

interface Props {
  label: string;
  testId: string;
  availableTags: GameTag[];
  selectedTags: GameTag[];
  onChange: (tags: GameTag[]) => void;
  helpText?: React.ReactNode;
}

export const GameTagPillSelector: React.FC<Props> = ({ label, testId, availableTags, selectedTags, onChange, helpText }) => {
  const isSelected = (tag: GameTag): boolean => selectedTags.some((selected) => selected.id === tag.id);

  const unselectedTags = availableTags.filter((tag) => !isSelected(tag));

  const addTag = (tagId: string): void => {
    const tag = availableTags.find((candidate) => String(candidate.id) === tagId);
    if (tag) {
      onChange([...selectedTags, tag]);
    }
  };

  const removeTag = (tag: GameTag): void => {
    onChange(selectedTags.filter((selected) => selected.id !== tag.id));
  };

  return (
    <div className="form-group">
      <label className="col-sm-3 control-label">{label}</label>
      <div className="col-sm-10">
        {selectedTags.length === 0 && <div className="text-muted mb-2">{t("game-tag-selector.none-selected", undefined, "None selected")}</div>}
        {selectedTags.length > 0 && (
          <div className="mb-2">
            {selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="badge badge-primary mr-1 mb-1 game-tag-pill"
              >
                {tag.name ?? tag.nameId}
                <button
                  type="button"
                  className="game-tag-pill-remove text-white ml-2"
                  data-testid={`${testId}-remove-${tag.id}`}
                  onClick={() => removeTag(tag)}
                  aria-label={t("game-tag-selector.remove-tag", { name: tag.name ?? tag.nameId }, `Remove ${tag.name ?? tag.nameId}`)}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}

        <select
          className="form-control"
          value=""
          data-testid={`${testId}-add-select`}
          onChange={(event) => {
            if (event.target.value) {
              addTag(event.target.value);
            }
          }}
          disabled={unselectedTags.length === 0}
        >
          <option value="">
            {unselectedTags.length === 0
              ? t("game-tag-selector.all-selected", undefined, "All tags selected")
              : t("game-tag-selector.add-tag", undefined, "Add a tag...")}
          </option>
          {unselectedTags.map((tag) => (
            <option
              key={tag.id}
              value={String(tag.id)}
            >
              {tag.name ?? tag.nameId}
            </option>
          ))}
        </select>

        {helpText && <span className="help">{helpText}</span>}
      </div>
    </div>
  );
};
